import {
  type Currency,
  type DropTableItem,
  isCatalogItemId,
  parseAmount,
  RESULT_RANGE,
} from "@loot-os/api-contract";
import type { EntryRef, Weighting } from "@loot-os/core/boxProcessing/weighting";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useMemo } from "react";
import {
  BOX_ART_FIELDS,
  type BoxArtUrls,
  type BoxEditorData,
  boxArtUrls,
  type DropTableRowInput,
  type EditorDropTableEntry,
} from "./boxEditor";
import { dropTableEvClient, suggestedPrice } from "./format";

/** What the catalog picker hands back — the catalog fields a row displays. */
export interface PickedCatalogItem {
  catalogItemId: string;
  name: string;
  imageUrl: string;
  /** Integer US cents — `catalog_items.value`, the internal unit. */
  value: number;
  /** Integer US cents — `catalog_items.market_price`, shown and sorted on, never published. */
  marketPriceCents: number | null;
}

/** What the listing picker hands back. */
export interface PickedListing {
  listingId: string;
  name: string;
  imageUrl: string;
  /**
   * Integer US cents, and the figure publish will store: the vendor's asking price plus the US
   * shipping that price excludes. The search procedure computes it, so the editor and the
   * published row cannot disagree.
   */
  value: number;
  /** The vendor's asking price, the listing's market price. */
  marketPriceCents: number | null;
}

/** What the processor stamped on a stored row; shown as badges, never authored. */
export interface DraftRowFlags {
  isLimited: boolean;
  limitedQuantity: number | null;
  isGemSubstitute: boolean;
  /** Out of stock, so the processor set its weight to 0 until it is restocked. */
  isExcluded: boolean;
}

interface DraftRowBase {
  key: string;
  /**
   * The weight as typed, in percent: "10" is 100,000 parts per million. Four decimals reach one
   * part per million, so every weight the scale can hold can be typed.
   */
  weight: string;
  name: string;
  imageUrl: string;
  value: number;
  marketPriceCents: number | null;
  /** From the stored version; a fresh pick has none until the processor runs. */
  flags?: DraftRowFlags;
}

/**
 * A catalog row mid-edit. Only the weight and the value override are typed: everything else is
 * copied from the catalog item the picker chose, so a row can never name an item the server
 * will reject. Both are strings because they are bound to an `<input>` — "" and "-" are states
 * a number can't hold.
 */
export interface CatalogDraftRow extends DraftRowBase, PickedCatalogItem {
  itemSource: "CATALOG";
  /**
   * What the operator typed, in the environment's currency; blank for no override. null until
   * the operator edits the field of a loaded row, which then publishes `storedOverrideCents`.
   */
  valueOverride: string | null;
  /**
   * The override the loaded version stores, in cents, or null. It is kept in cents because the
   * currency can still be the default when the draft is built, and a string formatted then
   * would publish at the wrong scale once the real currency loads.
   */
  storedOverrideCents: number | null;
}

/**
 * A vendor listing row mid-edit. It has no override: a listing row also stores the vendor's
 * asking price, which the withdrawal price check compares the live price against, and publish
 * reads both figures from the listing.
 */
export interface MarketplaceDraftRow extends DraftRowBase, PickedListing {
  itemSource: "MARKETPLACE";
}

export type DraftRow = CatalogDraftRow | MarketplaceDraftRow;

/** One percent in parts per million. */
const PPM_PER_PERCENT = RESULT_RANGE / 100;

/** A typed percent as parts per million; anything unreadable or negative is 0. */
export function percentToPpm(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n * PPM_PER_PERCENT) : 0;
}

/** Parts per million as the percent the weight field shows, with no trailing zeros. */
export function ppmToPercentInput(ppm: number): string {
  return String(Number((ppm / PPM_PER_PERCENT).toFixed(4)));
}

/** The id the row names, whichever source it has. */
export function rowItemId(row: DraftRow): string {
  return row.itemSource === "MARKETPLACE" ? row.listingId : row.catalogItemId;
}

/** Whether the row names something publish can resolve. */
export function rowHasItem(row: DraftRow): boolean {
  return row.itemSource === "MARKETPLACE"
    ? row.listingId !== ""
    : isCatalogItemId(row.catalogItemId);
}

/**
 * What makes two rows the same prize: the source and the id it names (the server compares a
 * listing by its `MP#` asset group id, which is one per listing id). null while the row names
 * nothing yet.
 */
export function rowIdentity(row: DraftRow): string | null {
  return rowHasItem(row) ? `${row.itemSource}:${rowItemId(row)}` : null;
}

/**
 * Keys of every row naming an item an earlier row already names. Publish refuses a table with
 * any ("drop table names … more than once"), because two rows would each carry part of one
 * item's weight.
 */
export function duplicateRowKeys(rows: DraftRow[]): Set<string> {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const row of rows) {
    const identity = rowIdentity(row);
    if (identity === null) continue;
    if (seen.has(identity)) repeated.add(row.key);
    else seen.add(identity);
  }
  return repeated;
}

/** The ids of one source that rows other than `exceptKey` already name, for a picker to refuse. */
export function takenItemIds(
  rows: DraftRow[],
  source: DraftRow["itemSource"],
  exceptKey: string,
): Set<string> {
  return new Set(
    rows
      .filter((row) => row.key !== exceptKey && row.itemSource === source && rowHasItem(row))
      .map(rowItemId),
  );
}

/**
 * Splits an appended import into the rows to add and the ones the table already names (by
 * this table's rows or an earlier imported line). Unresolved rows name nothing yet and are
 * always added; the picker refuses a repeat once they are re-picked.
 */
export function splitRepeatedRows<T extends DraftRow>(
  existing: DraftRow[],
  imported: T[],
): { added: T[]; repeated: T[] } {
  const seen = new Set(existing.map(rowIdentity).filter((id): id is string => id !== null));
  const added: T[] = [];
  const repeated: T[] = [];
  for (const row of imported) {
    const identity = rowIdentity(row);
    if (identity !== null && seen.has(identity)) {
      repeated.push(row);
      continue;
    }
    if (identity !== null) seen.add(identity);
    added.push(row);
  }
  return { added, repeated };
}

/** Names one row for the solver's dynamic pair, in the form the publish procedure resolves. */
export function rowEntryRef(row: DraftRow): EntryRef {
  return row.itemSource === "MARKETPLACE"
    ? { source: "MARKETPLACE", id: row.listingId }
    : { source: "CATALOG", id: row.catalogItemId };
}

/**
 * Where the box price comes from, one per weighting mode: the margin (`WEIGHT_TARGET`), the
 * operator (`MANUAL`), or the operator with the solver moving two rows' weights to reach it
 * (`PRICE_TARGET`).
 */
export type PriceMode = "margin" | "custom" | "targetPrice";

/** Boxed's default, and the 0.85 win rate every box published before the modes existed used. */
export const DEFAULT_MARGIN_PERCENT = 15;

/**
 * Whether the price publish would send is one the server accepts. The margin mode sends none,
 * because `WEIGHT_TARGET` computes it; the other two send the typed price, which must be a
 * positive number of cents, and a blank field reads as 0.
 */
export function hasPublishPrice(priceMode: PriceMode, price: number | null): boolean {
  return priceMode === "margin" || (price !== null && price > 0);
}

/** The solver moves two rows and no more, so a third pick replaces the oldest. */
export const DYNAMIC_ROW_COUNT = 2;

/** Why the publish button is off, on the button itself, or undefined when it is on. */
export function publishBlockedReason(
  priceMode: PriceMode,
  sumOk: boolean,
  weightingOk: boolean,
  priceOk: boolean,
): string | undefined {
  if (!weightingOk) return "pick a margin, and two rows for the solver to move";
  if (!priceOk) {
    return priceMode === "targetPrice"
      ? "enter a target price above zero"
      : "enter a box price above zero";
  }
  if (sumOk) return undefined;
  return priceMode === "targetPrice"
    ? `pick ${DYNAMIC_ROW_COUNT} rows for the solver, and leave them weight to take`
    : `weights must sum to exactly 100% (${formatNumber(RESULT_RANGE)})`;
}

export interface DropTableDraft {
  rows: DraftRow[];
  /** One edit of the whole table: an import, a normalize. */
  setRows: (recipe: (rows: DraftRow[]) => DraftRow[]) => void;
  setWeight: (key: string, weight: string) => void;
  setValueOverride: (key: string, value: string) => void;
  pickItem: (key: string, item: PickedCatalogItem) => void;
  pickListing: (key: string, listing: PickedListing) => void;
  addCatalogRow: () => void;
  addListingRow: () => void;
  removeRow: (key: string) => void;
  /** One edit removing every named row; a table left empty gets the blank row a new box has. */
  removeRows: (keys: ReadonlySet<string>) => void;
  /** Numeric items in editor order — what the preview and the odds column render. */
  parsed: DropTableItem[];
  /** What publish sends: the server reads name, image and value from the catalog or the listing. */
  publishRows: DropTableRowInput[];
  /** What publish sends beside the rows; null while the target-price pair is incomplete. */
  weighting: Weighting | null;
  weightSum: number;
  /** The exact sum under two modes; under target price, room left for the solver. */
  sumOk: boolean;
  ev: number;
  /** null when the margin is out of range; 0 when a typed price is blank. */
  price: number | null;
  /** Every row names a catalog item or a listing; a legacy row does not until it is re-picked. */
  itemsOk: boolean;
  /** Rows loaded from a version published before drop tables named catalog items. */
  legacyRowCount: number;
  /** Rows an import could not resolve to a catalog item this box may name. */
  unresolvedRowCount: number;
  /** Rows naming an item an earlier row already names; publish refuses while any remain. */
  duplicateKeys: Set<string>;
  /** How far the value of one open spreads around the EV, in cents. */
  stdDev: number;
  priceMode: PriceMode;
  setPriceMode: (mode: PriceMode) => void;
  marginPercent: string;
  setMarginPercent: (value: string) => void;
  enteredPrice: string;
  setEnteredPrice: (value: string) => void;
  /** The rows the solver may move, by draft key. Exactly two under target price. */
  dynamicKeys: string[];
  toggleDynamic: (key: string) => void;
}

/**
 * A stored row from before drop tables named catalog items: it has an id, but not one the
 * catalog can resolve, so publishing it is refused. The editor keeps it as loaded, name and
 * value included, so the operator can see what the prize was while picking its replacement.
 */
export function isLegacyRow(row: Pick<CatalogDraftRow, "catalogItemId">): boolean {
  return row.catalogItemId !== "" && !isCatalogItemId(row.catalogItemId);
}

/** A legacy row off the union: a listing id is never a catalog uuid, so only catalog rows qualify. */
function isLegacyDraftRow(row: DraftRow): boolean {
  return row.itemSource === "CATALOG" && isLegacyRow(row);
}

/**
 * A row an import named but could not resolve: the id is kept as its name, so the operator sees
 * what was pasted while picking the item it should have been.
 */
export function isUnresolvedRow(row: DraftRow): boolean {
  return row.itemSource === "CATALOG" && row.catalogItemId === "" && row.name !== "";
}

/** The pick a row still needs, as the picker's prompt, or undefined for a row with none. */
export function repickPrompt(row: DraftRow): string | undefined {
  return isLegacyDraftRow(row) || isUnresolvedRow(row) ? `Replace “${row.name}”…` : undefined;
}

/**
 * A stored entry as a draft row. A catalog row loads the override the version stores, so a
 * republish that leaves the field alone sends the same override and the row keeps its value.
 */
export function toRow(item: EditorDropTableEntry, key: string = crypto.randomUUID()): DraftRow {
  const base = {
    key,
    name: item.name,
    imageUrl: item.imageUrl,
    value: item.value,
    weight: ppmToPercentInput(item.weight),
    marketPriceCents: item.marketPriceCents ?? null,
    flags: {
      isLimited: item.isLimited ?? false,
      limitedQuantity: item.limitedQuantity ?? null,
      isGemSubstitute: item.isGemSubstitute ?? false,
      isExcluded: item.effectiveWeight === 0,
    },
  };
  if (item.itemSource === "MARKETPLACE") {
    return { ...base, itemSource: "MARKETPLACE", listingId: item.itemId };
  }
  return {
    ...base,
    itemSource: "CATALOG",
    catalogItemId: item.itemId,
    valueOverride: null,
    storedOverrideCents: item.valueOverrideCents ?? null,
  };
}

/**
 * The override in cents: the typed figure once the operator has edited it, else the stored one,
 * and only when it is above zero, because publish sends no other override.
 */
function overrideCents(row: CatalogDraftRow, currency: Currency): number | null {
  const cents =
    row.valueOverride === null ? row.storedOverrideCents : parseAmount(row.valueOverride, currency);
  return cents != null && cents > 0 ? cents : null;
}

export function emptyCatalogRow(): CatalogDraftRow {
  return {
    key: crypto.randomUUID(),
    itemSource: "CATALOG",
    catalogItemId: "",
    name: "",
    imageUrl: "",
    value: 0,
    marketPriceCents: null,
    weight: "0",
    valueOverride: "",
    storedOverrideCents: null,
  };
}

function emptyListingRow(): MarketplaceDraftRow {
  return {
    key: crypto.randomUUID(),
    itemSource: "MARKETPLACE",
    listingId: "",
    name: "",
    imageUrl: "",
    value: 0,
    marketPriceCents: null,
    weight: "0",
  };
}

/**
 * The value a row publishes at: its override when publish sends one, else the item's own. The
 * preview's odds, EV and price read this, so they match what the server stores.
 */
export function rowValueCents(row: DraftRow, currency: Currency): number {
  return row.itemSource === "CATALOG" ? (overrideCents(row, currency) ?? row.value) : row.value;
}

/** One draft row in the shape the publish procedure takes. */
export function toPublishRow(row: DraftRow, weight: number, currency: Currency): DropTableRowInput {
  if (row.itemSource === "MARKETPLACE") {
    return { source: "MARKETPLACE", listingId: row.listingId, weight };
  }
  const override = overrideCents(row, currency);
  return {
    source: "CATALOG",
    catalogItemId: row.catalogItemId,
    weight,
    ...(override != null ? { valueCents: override } : {}),
  };
}

/** The mode a stored config was published under, so a reopened box keeps it. */
function initialPriceMode(weighting: Weighting): PriceMode {
  if (weighting.type === "PRICE_TARGET") return "targetPrice";
  return weighting.type === "MANUAL" ? "custom" : "margin";
}

/** The rows a stored PRICE_TARGET config names, matched back to the draft keys. */
export function initialDynamicKeys(rows: DraftRow[], weighting: Weighting): string[] {
  if (weighting.type !== "PRICE_TARGET") return [];
  return weighting.dynamicItems.flatMap((ref) => {
    const row = rows.find((r) => r.itemSource === ref.source && rowItemId(r) === ref.id);
    return row ? [row.key] : [];
  });
}

/**
 * Everything the box editor lets an operator change before saving: the metadata the header's
 * Save writes, and the drop table and pricing a publish writes. It is plain data, so the draft
 * store can keep it in localStorage and hold every earlier state for undo.
 */
export interface BoxDraftState {
  name: string;
  imageUrl: string;
  /** The rest of the box art: background, overlay and the mobile set. */
  art: BoxArtUrls;
  rows: DraftRow[];
  priceMode: PriceMode;
  marginPercent: string;
  enteredPrice: string;
  dynamicKeys: string[];
}

/**
 * The draft a box starts from: what the server stores. Row keys are the version and position,
 * not random, so the same box always yields the same draft and an edit that is undone back to
 * it reads as no change.
 */
export function initialBoxDraft(box: BoxEditorData, currency: Currency): BoxDraftState {
  const entries = box.currentVersionRow?.dropTable ?? [];
  const rows =
    entries.length > 0
      ? entries.map((entry, i) => toRow(entry, `v${box.currentVersion}-${i}`))
      : [{ ...emptyCatalogRow(), key: "new-0" }];
  const { weighting, priceCents } = box;
  return {
    name: box.name,
    imageUrl: box.imageUrl,
    art: boxArtUrls(box),
    rows,
    priceMode: initialPriceMode(weighting),
    marginPercent: String(
      "marginPercent" in weighting ? weighting.marginPercent : DEFAULT_MARGIN_PERCENT,
    ),
    // The plain number the currency displays, because the price field is an `<input type=number>`.
    enteredPrice: priceCents > 0 ? String(priceCents / currency.multiplier) : "",
    dynamicKeys: initialDynamicKeys(rows, weighting),
  };
}

/** Whether the metadata differs from the stored box: what the header's Save would write. */
export function isMetaChanged(state: BoxDraftState, box: BoxEditorData): boolean {
  return (
    state.name !== box.name ||
    state.imageUrl !== box.imageUrl ||
    // A draft persisted before the art slots existed has no `art`, which is no change.
    BOX_ART_FIELDS.some((field) => (state.art?.[field] ?? box[field]) !== box[field])
  );
}

/** Whether the table or its pricing differs from the start: what a publish would write. */
export function isTableChanged(state: BoxDraftState, initial: BoxDraftState): boolean {
  return (
    state.priceMode !== initial.priceMode ||
    state.marginPercent !== initial.marginPercent ||
    state.enteredPrice !== initial.enteredPrice ||
    JSON.stringify(state.dynamicKeys) !== JSON.stringify(initial.dynamicKeys) ||
    JSON.stringify(state.rows) !== JSON.stringify(initial.rows)
  );
}

/**
 * The spread of one open's value around the EV, in the table's own unit: the square root of
 * Σ p·(v − EV)², with p the weight over the million.
 */
export function dropTableStdDev(items: { weight: number; value: number }[], ev: number): number {
  const variance = items.reduce(
    (sum, item) => sum + (item.weight / RESULT_RANGE) * (item.value - ev) ** 2,
    0,
  );
  return Math.sqrt(variance);
}

/** The margins the margin mode's range is quoted between: boxed's editor slid from 5 to 30. */
export const MARGIN_RANGE_PERCENT = [5, 30] as const;

/** The box price at each end of `MARGIN_RANGE_PERCENT`, in cents. */
export function priceRangeAtMargins(ev: number): [number, number] {
  const [low, high] = MARGIN_RANGE_PERCENT;
  return [suggestedPrice(ev, 1 - low / 100), suggestedPrice(ev, 1 - high / 100)];
}

/**
 * The drop table and its pricing, derived from one draft state, plus setters that turn each
 * edit into a new state. `edit` is the draft store's: every setter goes through it, so every
 * change is one undo step, and `coalesce` folds a run of keystrokes in one field into one.
 */
export function useDropTableDraft(
  state: BoxDraftState,
  edit: (recipe: (state: BoxDraftState) => BoxDraftState, coalesce?: string) => void,
  currency: Currency,
): DropTableDraft {
  const { rows, priceMode, marginPercent, enteredPrice, dynamicKeys } = state;
  const setRows = (recipe: (rows: DraftRow[]) => DraftRow[], coalesce?: string) =>
    edit((prev) => ({ ...prev, rows: recipe(prev.rows) }), coalesce);

  const parsed = useMemo<DropTableItem[]>(
    () =>
      rows.map((r) => ({
        itemId: rowItemId(r),
        itemSource: r.itemSource,
        name: r.name,
        imageUrl: r.imageUrl,
        value: rowValueCents(r, currency),
        weight: percentToPpm(r.weight),
      })),
    [rows, currency],
  );

  const weightSum = parsed.reduce((sum, item) => sum + item.weight, 0);
  const dynamicWeight = rows.reduce(
    (sum, row, i) => (dynamicKeys.includes(row.key) ? sum + (parsed[i]?.weight ?? 0) : sum),
    0,
  );
  // Under target price the solver fills the two dynamic rows, so only the rest must leave room.
  const sumOk =
    priceMode === "targetPrice"
      ? dynamicKeys.length === DYNAMIC_ROW_COUNT && weightSum - dynamicWeight < RESULT_RANGE
      : weightSum === RESULT_RANGE;
  const ev = weightSum > 0 ? dropTableEvClient(parsed) : 0;
  const marginPercentNum = Number.parseFloat(marginPercent);
  const marginOk = marginPercentNum >= 0 && marginPercentNum < 100;
  // A typed price is in the environment's currency and held as cents like everything else.
  const price =
    priceMode === "margin"
      ? marginOk
        ? suggestedPrice(ev, 1 - marginPercentNum / 100)
        : null
      : (parseAmount(enteredPrice, currency) ?? 0);
  const itemsOk = rows.every(rowHasItem);

  const [firstDynamic, secondDynamic] = rows
    .filter((row) => dynamicKeys.includes(row.key))
    .map(rowEntryRef);

  const publishWeighting = (): Weighting | null => {
    if (priceMode === "custom") return { type: "MANUAL" };
    if (!marginOk) return null;
    if (priceMode === "margin") return { type: "WEIGHT_TARGET", marginPercent: marginPercentNum };
    if (!firstDynamic || !secondDynamic) return null;
    return {
      type: "PRICE_TARGET",
      marginPercent: marginPercentNum,
      dynamicItems: [firstDynamic, secondDynamic],
    };
  };

  return {
    rows,
    setRows,
    setWeight: (key, weight) => {
      setRows((prev) => prev.map((r) => (r.key === key ? { ...r, weight } : r)), `weight:${key}`);
    },
    setValueOverride: (key, valueOverride) => {
      setRows(
        (prev) =>
          prev.map((r) =>
            r.key === key && r.itemSource === "CATALOG" ? { ...r, valueOverride } : r,
          ),
        `override:${key}`,
      );
    },
    // A pick replaces the whole row but its weight, dropping any override typed for the old item.
    // An item another row already names is refused here too; the picker greys it out first.
    pickItem: (key, item) => {
      setRows((prev) =>
        takenItemIds(prev, "CATALOG", key).has(item.catalogItemId)
          ? prev
          : prev.map((r) =>
              r.key === key
                ? {
                    ...item,
                    key: r.key,
                    weight: r.weight,
                    itemSource: "CATALOG",
                    valueOverride: "",
                    storedOverrideCents: null,
                  }
                : r,
            ),
      );
    },
    pickListing: (key, listing) => {
      setRows((prev) =>
        takenItemIds(prev, "MARKETPLACE", key).has(listing.listingId)
          ? prev
          : prev.map((r) =>
              r.key === key
                ? { ...listing, key: r.key, weight: r.weight, itemSource: "MARKETPLACE" }
                : r,
            ),
      );
    },
    addCatalogRow: () => setRows((prev) => [...prev, emptyCatalogRow()]),
    addListingRow: () => setRows((prev) => [...prev, emptyListingRow()]),
    removeRow: (key) => {
      edit((prev) => ({
        ...prev,
        rows: prev.rows.filter((r) => r.key !== key),
        dynamicKeys: prev.dynamicKeys.filter((k) => k !== key),
      }));
    },
    removeRows: (keys) => {
      edit((prev) => {
        const kept = prev.rows.filter((r) => !keys.has(r.key));
        return {
          ...prev,
          rows: kept.length > 0 ? kept : [emptyCatalogRow()],
          dynamicKeys: prev.dynamicKeys.filter((k) => !keys.has(k)),
        };
      });
    },
    parsed,
    publishRows: rows.map((row, i) => toPublishRow(row, parsed[i]?.weight ?? 0, currency)),
    weighting: publishWeighting(),
    weightSum,
    sumOk,
    ev,
    stdDev: weightSum > 0 ? dropTableStdDev(parsed, ev) : 0,
    price,
    itemsOk,
    legacyRowCount: rows.filter(isLegacyDraftRow).length,
    unresolvedRowCount: rows.filter(isUnresolvedRow).length,
    duplicateKeys: duplicateRowKeys(rows),
    priceMode,
    setPriceMode: (mode) => edit((prev) => ({ ...prev, priceMode: mode })),
    marginPercent,
    setMarginPercent: (value) => edit((prev) => ({ ...prev, marginPercent: value }), "margin"),
    enteredPrice,
    setEnteredPrice: (value) => edit((prev) => ({ ...prev, enteredPrice: value }), "price"),
    dynamicKeys,
    toggleDynamic: (key) => {
      edit((prev) => ({
        ...prev,
        dynamicKeys: prev.dynamicKeys.includes(key)
          ? prev.dynamicKeys.filter((k) => k !== key)
          : [...prev.dynamicKeys, key].slice(-DYNAMIC_ROW_COUNT),
      }));
    },
  };
}
